import { Controller } from "@hotwired/stimulus"
import RequestHelper from "../helpers/request_helper"

function fetchJson(url, options) {
  return fetch(url, options).then((response) => response.json().then((data) => ({ ok: response.ok, data })));
}

// Builds the add-table's select-all header cell as real elements rather than an HTML string -
// see the comment on onDraw for why this can't be placed directly in the Haml.
function buildSelectAllHeader() {
  const wrapper = document.createElement("div");
  wrapper.className = "checkbox inline";

  const label = document.createElement("label");
  label.className = "boolean";
  label.setAttribute("for", "select-all-members");

  const input = document.createElement("input");
  input.className = "boolean";
  input.id = "select-all-members";
  input.name = "select-all-members";
  input.type = "checkbox";
  input.value = "1";
  input.dataset.track = "Weekly Gift Activity Digest";

  label.appendChild(input);
  label.appendChild(document.createTextNode(" All"));
  wrapper.appendChild(label);
  return wrapper;
}

// Paired with the shared `datatable` controller on the same mount: that one owns the DataTables
// lifecycle, this one owns the search-input GA tracking attrs, the add-table's select-all header
// injection, and the `.remove-user`/seat-info behaviour, reached through DataTables' `draw.dt`.
//
// Role selection and delete-collaborator belong to team_member_roles_controller.js, mounted
// alongside this one on the same element. #add-members-button is handled here rather than in
// team_editor_controller.js because it needs this table's own checked rows as its search results.
export default class extends Controller {
  static get targets() { return ["table", "memberSearch"]; }
  static get values() { return { addMode: Boolean }; }

  connect() {
    this.onDraw = this.onDraw.bind(this);
    this.onRemoveUser = this.onRemoveUser.bind(this);

    if (!this.hasTableTarget) { return; }

    $(this.tableTarget).on("draw.dt.teamMemberTable", this.onDraw);
    this.onDraw();

    // `.remove-user` only ever exists inside a row DataTables just drew - never at connect() -
    // so this stays a delegated jQuery listener rather than a data-action (Pattern 1: this.element
    // scoping keeps the no-op-on-absent-mount property the same way the old find() did).
    $(this.element).on("click.teamMemberTable", ".remove-user", this.onRemoveUser);
  }

  disconnect() {
    if (!this.hasTableTarget) { return; }

    $(this.tableTarget).off(".teamMemberTable");
    $(this.element).off(".teamMemberTable");
  }

  // The shared `datatable` controller already covers the pagination hide/show, so only the
  // page-specific pieces are here: GA tracking attrs on the search box, and (add-table only) the
  // select-all header, which errors if placed directly in the HTML.
  onDraw() {
    const wrapper = this.tableTarget.closest(".dataTables_wrapper");
    const searchInput = wrapper?.querySelector(".dataTables_filter input[type='search']");
    if (searchInput) {
      searchInput.setAttribute("data-track", "Search");
      searchInput.setAttribute("data-trackcategory", "Account Settings | Member");
    }

    if (this.addModeValue) {
      const headerCell = this.tableTarget.querySelector("thead tr th:first-child");
      if (headerCell) { headerCell.replaceChildren(buildSelectAllHeader()); }
    }
  }

  // Undebounced, alongside the shared `datatable` controller's own 500ms-debounced binding on the
  // same input. Both stay: the timing difference is observable.
  onSearchKeyup(event) {
    $(this.tableTarget).DataTable().search(event.currentTarget.value).draw();
  }

  updateInviteMemberButton() {
    const button = document.getElementById("invite-member");
    if (button) { button.disabled = false; }
    $(".availability-tooltip-wrapper").tooltip("disable");
  }

  updateSeatAvailabilityInline() {
    const container = document.querySelector(".seat-limiting-info");
    if (!container) { return; }
    const url = container.dataset.url;

    fetch(url, RequestHelper.defaultRequestOptions()).then((response) => response.json()).then((info) => {
      const pluralizeSeats = info.max_seats === 1 ? "seat" : "seats";

      if (info.seat_limiting_enabled) {
        container.textContent = `${info.available_seats} of ${info.max_seats} ${pluralizeSeats} remaining`;
      }

      if (info.available_seats > 0) { this.updateInviteMemberButton(); }
    });
  }

  updateSeatInformation() {
    const container = document.querySelector(".seat-limiting-info");
    if (!container) { return; }
    const url = container.dataset.url;

    fetch(url, RequestHelper.defaultRequestOptions()).then((response) => response.json()).then((info) => {
      if (!info.seat_limiting_enabled) { return; }

      const totalSeats = document.getElementById("total-seats");
      if (totalSeats) { totalSeats.value = info.max_seats; }

      const seatsUsed = document.getElementById("seats-used");
      if (seatsUsed) { seatsUsed.textContent = info.max_seats - info.available_seats; }

      const seatsAvailable = document.getElementById("seats-available");
      if (seatsAvailable) { seatsAvailable.textContent = info.available_seats; }
    });
  }

  onRemoveUser(event) {
    event.preventDefault();

    const link = event.currentTarget;
    const url = link.dataset.url;
    const result = window.confirm(`Are you sure you want to remove ${link.dataset.user} from your team?`);

    if (!result) { return; }

    const options = RequestHelper.defaultDeleteOptions();
    fetchJson(url, options).then(({ ok, data }) => {
      const errorEl = document.getElementById("team-members-error");

      if (!ok) {
        if (errorEl) {
          errorEl.textContent = data.error;
          errorEl.style.display = "";
        }
        return;
      }

      if (errorEl) { errorEl.style.display = "none"; }

      // jQuery's fadeOut animates opacity/height before removing the row; kept on jQuery since
      // that animation has no native one-liner equivalent and isn't itself a DOM-correctness risk.
      $(link).closest("tr").fadeOut();

      const isAdminView = document.getElementById("team-members-datatable")?.dataset.admin === "true";
      if (isAdminView) { this.updateSeatInformation(); } else { this.updateSeatAvailabilityInline(); }
    });
  }

  // Lives here rather than in team_editor_controller.js because the rows it reads are this
  // table's own. Only the subdomain add-members table renders this button, always with
  // data-datatype="html" - a full-page redirect on success - so that is the only branch needed.
  onAddMembers(event) {
    event.preventDefault();

    const button = event.currentTarget;
    const checkboxes = this.element.querySelectorAll("input[type=checkbox]:checked");
    const url = button.href;

    button.classList.add("disabled");

    const idsToAdd = Array.from(checkboxes).map((el) => ({
      email: el.dataset.email,
      role: el.closest(".team-member, tr")?.querySelector(".action select, .role select")?.value,
    }));

    if (idsToAdd.length === 0) {
      window.ltAlert({ message: "You must select at least one user to invite.", type: "toast-error", duration: 2500 });
      button.classList.remove("disabled");
      return;
    }

    const options = RequestHelper.defaultPostOptions();
    options.headers = Object.assign({}, options.headers, {
      "Content-Type": "application/json",
      "Accept": "text/html",
    });
    options.body = JSON.stringify({ invitations: idsToAdd });

    fetchJson(url, options).then(({ ok, data }) => {
      if (!ok) {
        button.classList.remove("disabled");
        window.ltAlert({ message: data.message, type: "toast-error", duration: 2500 });
        return;
      }

      // Our own controller's JSON response, same origin as this page - asserted before navigating
      // per the S3 rule rather than trusting `data.redirect_url` outright.
      if (RequestHelper.isSameOriginUrl(data.redirect_url)) {
        window.location.href = data.redirect_url;
      } else {
        button.classList.remove("disabled");
        window.ltAlert({ message: "An error occurred while completing this action.", type: "toast-error", duration: 2500 });
      }
    });
  }
}
